import { fraction, rateFor, toNet, toNumber } from "../../shared/money";
import { type BundleRow, type ItemPricing, type MaterialRow, type PriceListData, type PriceListSettings, pricedByGroup, priceLock, type SupplierPriceRow } from "./meta";
import { groupTagsName } from "./names";
import { type ItemRate, itemProfit, labourRateNet, missingCost, type NeedsCheck, needsCheck, partItem, pricedAs, type Profit, underMinimum } from "./pricing";
import { itemNumberIssues, sharedNames } from "./setup";
import { ageOf, hasAmount } from "./supplierPricing";

/*
 * What needs attention on an item, a bundle or a supplier, the same everywhere it's shown: its
 * window's "Needs attention" box, its list's mark, tint and filters, and Home. Used by the server
 * (Home) and the screens alike, so they can't disagree.
 */

type NumberIssues = ReturnType<typeof itemNumberIssues>;

// ---------------------------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------------------------

/** Each thing that can need attention on an item (its list's filters are these too). */
export type ItemProblemId = "noNumber" | "sharedNumber" | "unknown" | "sharedName" | "lockedChanged" | "low" | "partLoss" | "changed" | "check" | "costUpdate" | "pricesDue" | "noCost" | "names";

/**
 * Problems told from what only See costs shows (an item's pricing and groups, its costs, its supplier prices): without it, the Price
 * List hides them (costs.ts, MaterialsList's cost filters), so neither does Home count them.
 */
export const COST_PROBLEMS: ReadonlySet<ItemProblemId> = new Set(["low", "partLoss", "check", "costUpdate", "pricesDue", "noCost", "names"]);

export interface ItemProblem {
    id: ItemProblemId;
    /** A few words, for a list's mark ("No item number"). */
    text: string;
    /** Red, not amber: it can trip up accounting software (its item number) or loses money. */
    danger: boolean;
}

/** What the item checks need from the Price List's data: the screens' data, or the server's read of it. */
export type ItemData = Pick<
    PriceListData,
    "materials" | "bundles" | "unknownItems" | "taxRates" | "itemPricing" | "pricingGroups" | "nameTags" | "settings" | "costUpdates" | "labourInCost" | "supplierPrices" | "costFollows" | "lockedItems" | "lockedPriceChanged"
> & {
    changedInServiceM8: Pick<PriceListData["changedInServiceM8"], "material">;
};

/** What every item's checks share, worked out once (item numbers across everything, and lookups). */
export interface ItemContext {
    data: ItemData;
    numbers: NumberIssues;
    /** Active items sharing their name with another (setup.ts sharedNames): uuid -> the others. */
    names: ReadonlyMap<string, readonly MaterialRow[]>;
    unknown: ReadonlySet<string>;
    changed: ReadonlySet<string>;
    /** Locked items whose price or tax was changed in ServiceM8 since (locks.ts): red. */
    lockedPriceChanged: ReadonlySet<string>;
    groups: ReadonlyMap<string, PriceListData["pricingGroups"][number]>;
    /** Items whose supplier prices (with an amount) are due a check or out of date (Price List settings): how many of each. */
    pricesDue: ReadonlyMap<string, PricesDue>;
}

// One per data load: the same data asked again (each row, each warning) isn't worked out again.
const contexts = new WeakMap<object, ItemContext>();

/**
 * Each item's supplier prices (with an amount) due a check or out of date, in one pass (only items with some): only while its cost
 * follows them (a typed cost, or a kit's from its parts, doesn't depend on them).
 */
function pricesDueByItem(data: Pick<ItemData, "supplierPrices" | "settings" | "costFollows">): Map<string, PricesDue> {
    const out = new Map<string, PricesDue>();
    const following = new Set(data.costFollows);
    for (const p of data.supplierPrices) {
        if (!hasAmount(p) || !following.has(p.materialUuid)) continue;
        const age = ageOf(p.checkedAt, data.settings);
        if (age === "current") continue;
        const was = out.get(p.materialUuid) ?? { due: 0, outdated: 0 };
        out.set(p.materialUuid, { due: was.due + 1, outdated: was.outdated + (age === "outdated" ? 1 : 0) });
    }
    return out;
}

/**
 * Problems that don't tint an item's row in a list (its other marks say so): supplier prices to check, marked by its cost (a
 * Materials list's Cost column) and in its needs-attention mark, as they're for its suppliers more than for it.
 */
export const tintsRow = (p: ItemProblem) => p.id !== "pricesDue";

/** "Supplier price to check" (or prices): its pill and its list's mark, short. */
export const pricesDueText = (due: PricesDue) => (due.due === 1 ? "Supplier price to check" : "Supplier prices to check");

/** In full, on hover: "2 of its supplier prices need checking (1 out of date): its cost follows them." */
export function pricesDueDetail(due: PricesDue) {
    const which = due.due === 1 ? (due.outdated ? "Its supplier price is out of date" : "Its supplier price is due a check") : `${due.due} of its supplier prices need checking${due.outdated ? ` (${due.outdated} out of date)` : ""}`;
    return `${which}: its cost follows ${due.due === 1 ? "it" : "them"}.`;
}

/** The shared part of the item checks for this data (kept while the data is the same object). */
export function itemContext(data: ItemData): ItemContext {
    const known = contexts.get(data);
    if (known) return known;
    const ctx: ItemContext = {
        data,
        numbers: itemNumberIssues([...data.materials, ...data.bundles]),
        names: sharedNames(data.materials),
        unknown: new Set(data.unknownItems),
        changed: new Set(data.changedInServiceM8.material),
        lockedPriceChanged: new Set(data.lockedPriceChanged),
        groups: new Map(data.pricingGroups.map((g) => [g.id, g])),
        pricesDue: pricesDueByItem(data),
    };
    contexts.set(data, ctx);
    return ctx;
}

/** An item's tax rate (its own, else the default), as its pricing checks take it. */
const rateOfItem = (m: Pick<MaterialRow, "tax_rate_uuid">, ctx: ItemContext): ItemRate => {
    const rate = rateFor(m.tax_rate_uuid, ctx.data.taxRates);
    return { uuid: rate?.uuid ?? "", tax: fraction(rate) };
};

/** An item, checked: what needs attention (worst first), and the facts behind it that lists show. */
export interface ItemAssessment {
    problems: ItemProblem[];
    /** Its item number: none, or shared with another item or bundle (active items only). */
    numberIssue: "missing" | "duplicate" | null;
    /** Its pricing gives another price now (active items only). */
    check: NeedsCheck | null;
    /** What it makes on its cost (part and labour), as priced now and costed as estimated (pricing.ts itemProfit). */
    profit: Profit;
    /** The group whose name tags read differently in its name, if one does (active items only). */
    nameGroup: string | null;
}

/**
 * Everything that needs attention on an item, worst first: what can trip up accounting software
 * (its item number), what loses money, what's unknown or changed in ServiceM8, then tidying. An
 * archived item only needs looking at if it changed in ServiceM8 (e.g. archived there by mistake).
 * Making too little isn't counted when the price update waiting for it would fix that (one problem).
 */
export function assessItem(item: MaterialRow, ctx: ItemContext): ItemAssessment {
    const { settings } = ctx.data;
    // Its pricing works from the part's own cost; what it makes, from its whole cost (its labour's too).
    const m = partItem(ctx.data, item);
    const pricing = ctx.data.itemPricing[m.uuid];
    const rate = rateOfItem(m, ctx);
    const profit = itemProfit(ctx.data, item, rate.tax);
    const changed = ctx.changed.has(m.uuid);
    // Its locked price (or tax) changed in ServiceM8 (locks.ts): red, before the rest of what changed there.
    const lockedChanged: ItemProblem[] = ctx.lockedPriceChanged.has(m.uuid) && priceLock(ctx.data, m.uuid) ? [{ id: "lockedChanged", text: "Locked price changed in ServiceM8", danger: true }] : [];
    if (!m.active) {
        const problems: ItemProblem[] = changed ? [...lockedChanged, { id: "changed", text: "Changed in ServiceM8", danger: false }] : [];
        return { problems, numberIssue: null, check: null, profit, nameGroup: null };
    }
    const numberIssue = numberIssueOf(m.uuid, ctx);
    const check = needsCheck(m, rate, pricing, settings);
    const nameGroup = nameGroupOf(m, ctx);
    const problems: ItemProblem[] = [];
    if (numberIssue === "missing") problems.push({ id: "noNumber", text: "No item number", danger: true });
    if (numberIssue === "duplicate") problems.push({ id: "sharedNumber", text: "Item number shared with another item", danger: true });
    if (ctx.unknown.has(m.uuid)) problems.push({ id: "unknown", text: "Unknown item from ServiceM8", danger: false });
    problems.push(...lockedChanged);
    const low = makesTooLittle(item, rate, profit, check, ctx);
    if (low) problems.push({ id: "low", text: "Making too little", danger: true });
    // Its part sold at or below its cost, its labour making up for it (making too little already says enough): red, never wanted.
    else if (partAtCost(item, rate, profit, check, ctx)) problems.push({ id: "partLoss", text: "Part at or below cost", danger: true });
    if (changed) problems.push({ id: "changed", text: "Changed in ServiceM8", danger: false });
    if (check) problems.push({ id: "check", text: "Price to update", danger: false });
    // Its supplier price gives another cost now: never changed by itself (supplierPrices.ts costUpdates).
    if (m.uuid in ctx.data.costUpdates) problems.push({ id: "costUpdate", text: "Cost to update", danger: false });
    // Any of its supplier prices due a check, or out of date: amber (its cost may be out of step; its suppliers say so too).
    const due = ctx.pricesDue.get(m.uuid);
    if (due) problems.push({ id: "pricesDue", text: pricesDueText(due), danger: false });
    if (missingCost(m, pricing)) problems.push({ id: "noCost", text: "No cost (its pricing needs one)", danger: false });
    if (ctx.names.has(m.uuid)) problems.push({ id: "sharedName", text: "Name shared with another item", danger: false });
    if (nameGroup) problems.push({ id: "names", text: `Name to update (the ${nameGroup} group's tags)`, danger: false });
    return { problems, numberIssue, check, profit, nameGroup };
}

/** Its item number: none, or shared with another item or bundle. */
function numberIssueOf(uuid: string, ctx: ItemContext): ItemAssessment["numberIssue"] {
    if (ctx.numbers.missing.has(uuid)) return "missing";
    return ctx.numbers.clashes.has(uuid) ? "duplicate" : null;
}

/** The name of its group, when the group's name tags read differently in its name. */
function nameGroupOf(m: MaterialRow, ctx: ItemContext): string | null {
    const id = ctx.data.itemPricing[m.uuid]?.groupId;
    const group = id ? ctx.groups.get(id) : undefined;
    return group?.setsTags && groupTagsName(m.name, group.tags, ctx.data.nameTags, ctx.data.itemPricing[m.uuid]?.tagOverrides) !== null ? group.name : null;
}

/** What needs attention on an item (assessItem's problems). */
export const itemProblems = (m: MaterialRow, ctx: ItemContext) => assessItem(m, ctx).problems;

/**
 * In a group, a price, cost or name to update is its own business (shown in its list, updated from its bar), not something wrong;
 * but a price to update by an item's own pricing (it overrides the group's, or the group doesn't set pricing) isn't the group's
 * to update, so it counts.
 */
const beyondGroup = (problems: readonly ItemProblem[], pricing: ItemPricing) =>
    problems.filter((p) => p.id !== "names" && p.id !== "costUpdate" && (p.id !== "check" || !pricedByGroup(pricing)));

/** A group's items that need attention (the same checks as everywhere: assessItem, but not its own updates), each with its problems; red when any is. */
export function groupAttention(data: ItemData, groupId: string) {
    const ctx = itemContext(data);
    const problems = new Map<string, ItemProblem[]>();
    for (const m of data.materials) {
        const pricing = data.itemPricing[m.uuid];
        if (pricing?.groupId !== groupId) continue;
        const found = beyondGroup(assessItem(m, ctx).problems, pricing);
        if (found.length) problems.set(m.uuid, found);
    }
    return { problems, count: problems.size, danger: [...problems.values()].some((list) => list.some((p) => p.danger)) };
}

/** Every group's items needing attention, in one pass: how many, and whether any is red (a group none of whose items do isn't there). */
export function attentionByGroup(data: ItemData): Map<string, { count: number; danger: boolean }> {
    const ctx = itemContext(data);
    const out = new Map<string, { count: number; danger: boolean }>();
    for (const m of data.materials) {
        const pricing = data.itemPricing[m.uuid];
        const groupId = pricing?.groupId;
        if (!groupId) continue;
        const found = beyondGroup(assessItem(m, ctx).problems, pricing);
        if (!found.length) continue;
        const was = out.get(groupId) ?? { count: 0, danger: false };
        out.set(groupId, { count: was.count + 1, danger: was.danger || found.some((p) => p.danger) });
    }
    return out;
}

/** "3 items need attention". */
export const attentionText = (count: number) => `${count.toLocaleString()} ${count === 1 ? "item needs" : "items need"} attention`;

/**
 * Under the minimum markup on its cost, unless the price update waiting for it (its check) would fix that: taxed as it will be,
 * on its estimated cost (as everywhere: pricing.ts itemProfit; its cost to update is its own step).
 */
function makesTooLittle(m: MaterialRow, rate: ItemRate, profit: Profit, check: NeedsCheck | null, ctx: ItemContext) {
    const { settings } = ctx.data;
    if (profit.profitNet === null || !underMinimum(profit.markupPct, settings.minMarkup)) return false;
    if (!check) return true;
    const after = pricedAs(m, rate, ctx.data.itemPricing[m.uuid]);
    return underMinimum(itemProfit(ctx.data, { ...after.item, price: check.target }, after.tax, { was: m }).markupPct, settings.minMarkup);
}

/**
 * With labour time, its part's share of its price (its price less its labour as charged) at or below the part's cost (estimated, as
 * everywhere: pricing.ts itemProfit): never wanted, even when its labour makes the item as a whole pay. Unless the price update
 * waiting for it would fix that (as makesTooLittle). Without labour, the part is the whole item: making too little says it.
 */
function partAtCost(m: MaterialRow, rate: ItemRate, profit: Profit, check: NeedsCheck | null, ctx: ItemContext) {
    const { settings, itemPricing } = ctx.data;
    const minutes = itemPricing[m.uuid]?.labourMinutes ?? 0;
    if (minutes <= 0 || profit.profitNet === null || profit.partOnly) return false;
    // Its part's share of the price (before tax, at `tax`), less the part's cost.
    const partMade = (price: string, includesTax: boolean, tax: number, p: Profit) =>
        toNet(toNumber(price), includesTax, tax) - (labourRateNet(settings, tax) * minutes) / 60 - p.partNet;
    if (partMade(m.price, m.price_includes_taxes, rate.tax, profit) > 0.005) return false;
    if (!check) return true;
    const after = pricedAs(m, rate, itemPricing[m.uuid]);
    const next = itemProfit(ctx.data, { ...after.item, price: check.target }, after.tax, { was: m });
    return partMade(check.target, after.item.price_includes_taxes, after.tax, next) <= 0.005;
}

// ---------------------------------------------------------------------------------------------
// Bundles and suppliers
// ---------------------------------------------------------------------------------------------

/** Items in a bundle that are archived or gone (by name; "a deleted item" for one no longer here). */
export function archivedInBundle(b: Pick<BundleRow, "material_list">, items: ReadonlyMap<string, { name: string; active: boolean }>): string[] {
    return b.material_list.flatMap((l) => {
        const m = items.get(l.uuid);
        if (!m) return ["a deleted item"];
        return m.active ? [] : [m.name];
    });
}

/** What's wrong with a bundle, a few words each (worst first): its item number, archived items in it, changes from ServiceM8. */
export function bundleProblems(
    b: Pick<BundleRow, "uuid" | "material_list" | "active">,
    numbers: NumberIssues,
    items: ReadonlyMap<string, { name: string; active: boolean }>,
    changedInServiceM8: boolean,
): string[] {
    const problems: string[] = [];
    if (b.active && numbers.missing.has(b.uuid)) problems.push("No item number");
    if (b.active && numbers.clashes.has(b.uuid)) problems.push("Item number shared with another item");
    const archived = b.active ? archivedInBundle(b, items) : [];
    if (archived.length) problems.push(`${archived.length.toLocaleString()} archived ${archived.length === 1 ? "item" : "items"} in it`);
    if (changedInServiceM8) problems.push("Changed in ServiceM8");
    return problems;
}

/** ""Red" also uses it. Every item needs its own." (or bundle): the others with the same item number, by name. */
export function sharedNumberText(others: readonly string[], noun: "item" | "bundle") {
    const names = others.map((n) => `"${n}"`).join(", ");
    return `${names} also ${others.length === 1 ? "uses" : "use"} it. Every ${noun} needs its own.`;
}

/** "Another item (DL2) has the same name.": the others with its name, by item number. */
export function sharedNameText(others: readonly Pick<MaterialRow, "name" | "item_number">[]) {
    const which = others.map((o) => (o.item_number ? o.item_number : "an item with no item number")).join(", ");
    return `${others.length === 1 ? "Another item" : `${others.length} other items`} (${which}) ${others.length === 1 ? "has" : "have"} the same name.`;
}

/** A supplier's prices (for active items) due for a recheck, and how many of those are out of date (past `outdatedDays`). */
export interface PricesDue {
    due: number;
    outdated: number;
}

/** Each supplier's prices due for a recheck, in one pass (only suppliers with some). */
export function pricesDueBySupplier(
    prices: readonly Pick<SupplierPriceRow, "supplierUuid" | "materialUuid" | "checkedAt">[],
    activeItems: ReadonlySet<string>,
    settings: Pick<PriceListSettings, "recheckDays" | "outdatedDays">,
): Map<string, PricesDue> {
    const bySupplier = new Map<string, PricesDue>();
    for (const p of prices) {
        if (!activeItems.has(p.materialUuid)) continue;
        const age = ageOf(p.checkedAt, settings);
        if (age === "current") continue;
        const counts = bySupplier.get(p.supplierUuid) ?? { due: 0, outdated: 0 };
        counts.due++;
        if (age === "outdated") counts.outdated++;
        bySupplier.set(p.supplierUuid, counts);
    }
    return bySupplier;
}

/** What's wrong with a supplier, a few words each: changes from ServiceM8, prices due for a recheck. */
export function supplierProblems(active: boolean, due: PricesDue | undefined, changedInServiceM8: boolean): string[] {
    const problems: string[] = [];
    if (changedInServiceM8) problems.push("Changed in ServiceM8");
    if (active && due?.due) {
        const outdated = due.outdated ? ` (${due.outdated.toLocaleString()} out of date)` : "";
        problems.push(`${due.due.toLocaleString()} ${due.due === 1 ? "price" : "prices"} to recheck${outdated}`);
    }
    return problems;
}

/** Red, not amber: an active supplier with prices out of date (costs that may well be wrong). */
export const supplierUrgent = (active: boolean, due: PricesDue | undefined) => active && (due?.outdated ?? 0) > 0;

/** Red, not amber: an active bundle with no item number or a shared one (it can trip up accounting software). */
export const bundleNumberProblem = (b: Pick<BundleRow, "uuid" | "active">, numbers: NumberIssues) => b.active && (numbers.missing.has(b.uuid) || numbers.clashes.has(b.uuid));
